// Shared color logic for weekly summary and slot details

export const colorMap = {
  exceptional: 'rgba(33, 150, 243, 0.8)',
  ok: 'rgba(76, 175, 80, 0.8)',
  not_ok: 'rgba(255, 235, 59, 0.8)',
  bad: 'rgba(255, 152, 0, 0.8)',
  veryBad: 'rgba(244, 67, 54, 0.8)',
  overstaffed: 'rgba(244, 67, 54, 0.8)',
  unlikely_overstaffed: 'rgba(255, 152, 0, 0.8)',
  adequate: 'rgba(76, 175, 80, 0.8)',
  unlikely_understaffed: 'rgba(139, 195, 74, 0.8)',
  understaffed: 'rgba(33, 150, 243, 0.8)',
  not_important: 'rgba(158, 158, 158, 0.8)',
};

export function getExperienceColor(label: string): string {
  switch (label) {
    case 'Exceptional': return colorMap.exceptional;
    case 'Ok': return colorMap.ok;
    case 'Bad': return colorMap.veryBad;
    default: return '#777777';
  }
}

export function staffingColor(staffing_class: any, staffing_flag: any): string {
  if (staffing_class === 'above' && staffing_flag === 'None') {
    return colorMap.overstaffed;
  }
  if (staffing_class === 'above' && staffing_flag === 'unlikely') {
    return colorMap.unlikely_overstaffed;
  }
  if (staffing_class === 'within') {
    return colorMap.adequate;
  }
  if (staffing_class === 'below' && staffing_flag === 'unlikely') {
    return colorMap.unlikely_understaffed;
  }
  if (staffing_class === 'below' && staffing_flag === 'None') {
    return colorMap.understaffed;
  }
  return '#777777';
}

// QPI color stops for smooth gradient
// Use colorMap values for red, orange, yellow, green
function rgbaToHex(rgba: string): string {
  // Convert rgba(r,g,b,a) or rgb(r,g,b) to hex
  const result = rgba.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (!result) return '#000000';
  const r = parseInt(result[1]);
  const g = parseInt(result[2]);
  const b = parseInt(result[3]);
  return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

const RED = rgbaToHex(colorMap.veryBad);    // Red
const ORANGE = rgbaToHex(colorMap.bad);     // Orange
const YELLOW = rgbaToHex(colorMap.not_ok);  // Yellow
const GREEN = rgbaToHex(colorMap.ok);       // Green

export const QPI_COLOR_STOPS = [
  { value: 0, color: RED },    // 0% - Red
  { value: 40, color: RED },   // 40% - Red
  { value: 60, color: ORANGE },   // 60% - Orange
  { value: 80, color: YELLOW },   // 80% - Yellow
  { value: 97, color: GREEN },    // 97% - Green
  { value: 100, color: GREEN },   // 100% - Green
];

function interpolateColor(color1: string, color2: string, t: number): string {
  // color1, color2: hex strings, t: 0-1
  const c1 = parseInt(color1.slice(1), 16);
  const c2 = parseInt(color2.slice(1), 16);
  const r1 = (c1 >> 16) & 0xff, g1 = (c1 >> 8) & 0xff, b1 = c1 & 0xff;
  const r2 = (c2 >> 16) & 0xff, g2 = (c2 >> 8) & 0xff, b2 = c2 & 0xff;
  const r = Math.round(r1 + (r2 - r1) * t);
  const g = Math.round(g1 + (g2 - g1) * t);
  const b = Math.round(b1 + (b2 - b1) * t);
  return `rgb(${r},${g},${b}, 0.8)`;
}

export function getQpiColor(value: number): string {
  for (let i = 1; i < QPI_COLOR_STOPS.length; i++) {
    const prev = QPI_COLOR_STOPS[i - 1];
    const next = QPI_COLOR_STOPS[i];
    if (value <= next.value) {
      const t = (value - prev.value) / (next.value - prev.value);
      return interpolateColor(prev.color, next.color, t);
    }
  }
  return QPI_COLOR_STOPS[QPI_COLOR_STOPS.length - 1].color;
}

export function getQpiGradient(): string {
  // Returns a CSS linear-gradient string for use in efficiency scale backgrounds
  return `linear-gradient(to right, ${
    QPI_COLOR_STOPS.map(stop => `${stop.color} ${stop.value}%`).join(', ')
  })`;
} 
